/**
 * What every ag-grid in the app is built out of, beside the grid itself: the Gaia theme it wears,
 * the height it fills, the flag the legacy tables switch on, and when to ask for the next page.
 *
 * The grid is AppGrid.component, and what it looks like is agGrid.scss.
 */
import {useLayoutEffect, useState} from 'react';
import {AllCommunityModule, ModuleRegistry} from 'ag-grid-community';
import type {GridApi} from 'ag-grid-community';
import {gaiaTheme} from '@vsn-ux/gaia-styles/integrations/ag-grid';
import {launchDarkly} from 'utils/launchDarkly';

/* ------------------------------------------------------------------------------------------- *
 * The theme
 * ------------------------------------------------------------------------------------------- */

// ag-grid v33+ requires explicit module registration; Community only, so no licence key.
// Registering here covers every grid that goes through AppGrid. The my-tasks list does not
// yet, and registers on its own - the call is idempotent, so both standing is harmless.
ModuleRegistry.registerModules([AllCommunityModule]);

/**
 * A Gaia size token, scaled to this app's root.
 *
 * Gaia expresses its sizes in rem against a 16px root, while this app runs on a 10px one -
 * which is what --ga-base-scaling-factor (1.6, set by all-10pt.css) is for. Gaia's stylesheet
 * multiplies by it, so every size taken straight from a token has to do the same.
 */
export const gaiaSize = (token: string) => `calc(var(${token}) * var(--ga-base-scaling-factor, 1))`;

/**
 * Gaia's own ag-grid theme, as this app wears it. Anything that should look the same in every
 * grid belongs here rather than in a page's own stylesheet.
 */
export const gridTheme = gaiaTheme.withParams({
    // Gaia's own sizes, scaled to this app's root
    fontSize: gaiaSize('--ga-text-md-font-size'),
    dataFontSize: gaiaSize('--ga-text-md-font-size'),
    // the heading reads at the same size as the values under it, only heavier
    headerFontSize: gaiaSize('--ga-text-md-font-size'),
    cellHorizontalPadding: gaiaSize('--ga-size-spacing-04'),
    iconSize: gaiaSize('--ga-size-16'),
    wrapperBorderRadius: gaiaSize('--ga-size-8'),

    // a list reads as one plain surface; Gaia's theme would tint every other row
    oddRowBackgroundColor: 'var(--ga-color-white)',

    // Gaia's own row colours and corner. These were the my-tasks list's choices before it took
    // Gaia's theme; kept here in case any of them reads better than what Gaia specifies
    // rowHoverColor: 'var(--ga-color-surface-action-hover-2)',
    // selectedRowBackgroundColor: 'var(--ga-color-surface-selected)',
    // wrapperBorderRadius: '4px',

    // the dark bubble the shared Gaia tooltip uses everywhere else (see AppTooltip.component)
    tooltipBackgroundColor: 'var(--ga-color-surface-action-hover)',
    tooltipTextColor: 'var(--ga-color-text-on-action)',
    tooltipBorder: false
});

/* ------------------------------------------------------------------------------------------- *
 * The legacy tables' flag
 * ------------------------------------------------------------------------------------------- */

// swaps the legacy reactabular table for the ag-grid rendering of the same column map
export const AG_GRID_TABLE_FLAG = 'use-ag-grid-table';

// the grid's date filter only exists as the new calendar, so this is a prerequisite
export const NEW_CALENDAR_FLAG = 'fe-use-new-calendar';

/** Read at render: the pages switching on it are class components with no wrapper to hold it. */
export const isAgGridTableEnabled = (): boolean =>
    (launchDarkly.userClient?.variation(AG_GRID_TABLE_FLAG, false) ?? false)
    && (launchDarkly.userClient?.variation(NEW_CALENDAR_FLAG, false) ?? false);

/* ------------------------------------------------------------------------------------------- *
 * The height the grid fills
 * ------------------------------------------------------------------------------------------- */

// a grid this short is barely a list, but it still beats one with no rows in view at all
const MIN_HEIGHT = 200;

export interface AvailableHeightOptions {
    /** what the page keeps below the grid: a fixed action bar, the card's own padding */
    bottomGap?: number;
    /**
     * A block above the grid that shows and hides - a warning, a batch panel - moves the grid
     * down without the window resizing. This is the element to watch for that, as a selector
     * `closest()` finds from the grid itself.
     */
    watchSelector?: string;
    /**
     * How short the grid may be squeezed before it is allowed to overflow instead. Worth
     * dropping to 0 where bottomGap stands for something drawn over the page - a fixed action
     * bar - since a grid that overflows past one hides its last rows behind it.
     */
    minHeight?: number;
}

/**
 * The height a grid should fill: whatever is left of the viewport below its own top.
 *
 * An ag-grid virtualises its rows, so it owns its scrolling area and needs a height in pixels -
 * and what sits above it is rarely a fixed height.
 *
 * The element is passed in rather than held in a ref, so that a grid mounted later - once its
 * first page has landed, say - is measured when it appears. Pages hand this a callback ref.
 */
export function useAvailableHeight(element: HTMLElement | null, options: AvailableHeightOptions = {}): number | null {
    const {bottomGap = 0, watchSelector, minHeight = MIN_HEIGHT} = options;
    const [height, setHeight] = useState<number | null>(null);

    useLayoutEffect(() => {
        if (!element) return;

        const measure = () => {
            const top = element.getBoundingClientRect().top;
            setHeight(Math.max(minHeight, window.innerHeight - top - bottomGap));
        };

        measure();
        window.addEventListener('resize', measure);
        const watched = watchSelector ? element.closest(watchSelector) : null;
        const observer = watched && typeof ResizeObserver !== 'undefined'
            ? new ResizeObserver(measure)
            : null;
        if (watched && observer) {
            observer.observe(watched);
        }

        return () => {
            window.removeEventListener('resize', measure);
            observer?.disconnect();
        };
    }, [element, bottomGap, watchSelector, minHeight]);

    return height;
}

/* ------------------------------------------------------------------------------------------- *
 * Paging
 * ------------------------------------------------------------------------------------------- */

/**
 * Whether the rows drawn reach the last one the grid holds, which is what asks for the next
 * page. ag-grid renders a buffer past the viewport, so this turns true a few rows before the
 * bottom is actually on screen - early enough that the next page is there when it is reached.
 */
export function isLastRowRendered(api: Pick<GridApi, 'getLastDisplayedRowIndex' | 'getDisplayedRowCount'>): boolean {
    return api.getLastDisplayedRowIndex() >= api.getDisplayedRowCount() - 1;
}
